import React from 'react';
import { Button, Group, List, Modal, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Prescription } from '../../../../global/entities/Prescription/Prescription';

interface ValidateConfirmModalProps {
    opened: boolean;
    onClose: () => void;
    onConfirm: () => void;
    prescriptions: Prescription[];
    validatingHcpName: string;
    isLoading?: boolean;
    unitsLabel?: (unitID: number) => string | undefined;
}

export const ValidateConfirmModal: React.FC<ValidateConfirmModalProps> = ({
    opened,
    onClose,
    onConfirm,
    prescriptions,
    validatingHcpName,
    isLoading,
    unitsLabel,
}) => {
    const { t, i18n } = useTranslation();
    const now = new Date();
    const formattedNow = now.toLocaleString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
        dateStyle: 'medium',
        timeStyle: 'short',
    });

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('prescriptionHistory.validate.modalTitle')}
            size="md"
            centered
        >
            <Stack gap="sm">
                <Text size="sm" fw={600}>
                    {t('prescriptionHistory.validate.validatingBy', { name: validatingHcpName })}
                </Text>
                <Text size="sm" c="dimmed">
                    {t('prescriptionHistory.validate.timestamp', { timestamp: formattedNow })}
                </Text>
                <Text size="sm" mt="sm">
                    {t('prescriptionHistory.validate.summary', { count: prescriptions.length })}
                </Text>
                <List size="sm" withPadding>
                    {prescriptions.map((p) => (
                        <List.Item key={p.id}>
                            <Text span fw={500}>
                                {p.drugName}
                            </Text>{' '}
                            <Text span c="dimmed">
                                — {p.dose} {unitsLabel?.(p.unitID) ?? ''}
                            </Text>
                        </List.Item>
                    ))}
                </List>
                <Group justify="flex-end" mt="md">
                    <Button variant="outline" onClick={onClose} disabled={isLoading}>
                        {t('button.cancel')}
                    </Button>
                    <Button onClick={onConfirm} loading={isLoading}>
                        {t('prescriptionHistory.validate.confirmCta')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
